import React, { useState } from 'react';
import './Docs.css';

const SECTIONS = [
  {
    id: 'mission',
    title: 'Our Mission',
    icon: '🎯',
    subsections: [
      { id: 'vision', title: 'Vision' },
      { id: 'problem', title: 'The Problem' },
      { id: 'solution', title: 'Our Solution' },
      { id: 'network', title: 'The Network' },
    ]
  },
  {
    id: 'platform',
    title: 'Platform Guide',
    icon: '⚙️',
    subsections: [
      { id: 'overview', title: 'Platform Overview' },
      { id: 'campaigns', title: 'Research Campaigns' },
      { id: 'uploading', title: 'Uploading Data' },
      { id: 'phi-protection', title: 'PHI Protection' },
      { id: 'manage-campaigns', title: 'Managing Campaigns' },
      { id: 'predict', title: 'Outcome Prediction' },
      { id: 'benchmark', title: 'My Practice' },
      { id: 'querying', title: 'Query & Analytics' },
      { id: 'users', title: 'Users & Centers' },
    ]
  },
  {
    id: 'data-standard',
    title: 'SpineBase Standard',
    icon: '📋',
    subsections: [
      { id: 'spinebase-overview', title: 'Overview' },
      { id: 'data-categories', title: 'Data Categories' },
      { id: 'required-fields', title: 'Required Fields' },
      { id: 'validation', title: 'Validation Rules' },
      { id: 'transformation', title: 'Data Transformation' },
    ]
  },
  {
    id: 'api',
    title: 'API Reference',
    icon: '🔌',
    subsections: [
      { id: 'api-auth', title: 'Authentication' },
      { id: 'api-campaigns', title: 'Campaigns API' },
      { id: 'api-patients', title: 'Patients API' },
      { id: 'api-predict', title: 'Prediction API' },
      { id: 'api-benchmark', title: 'Benchmark API' },
      { id: 'api-centers', title: 'Centers API' },
    ]
  },
  {
    id: 'privacy',
    title: 'Privacy & Legal',
    icon: '🔒',
    subsections: [
      { id: 'hipaa', title: 'HIPAA Compliance' },
      { id: 'gdpr', title: 'GDPR Compliance' },
      { id: 'anonymization', title: 'Anonymization' },
      { id: 'data-usage', title: 'Data Usage Agreement' },
      { id: 'irb', title: 'IRB & Institutional Review' },
    ]
  },
  {
    id: 'architecture',
    title: 'Architecture',
    icon: '🏗️',
    subsections: [
      { id: 'tech-stack', title: 'Technical Stack' },
      { id: 'data-model', title: 'Data Model' },
      { id: 'prediction-engine', title: 'Prediction Engine' },
      { id: 'web3-roadmap', title: '$SPINE Token & Web3' },
    ]
  },
  {
    id: 'for-surgeons',
    title: 'For Surgeons',
    icon: '👨‍⚕️',
    subsections: [
      { id: 'why-join', title: 'Why Join?' },
      { id: 'what-you-get', title: 'What You Receive' },
      { id: 'faq', title: 'FAQ' },
    ]
  },
];

const CONTENT = {
  // ═══════════════════ MISSION ═══════════════════
  vision: {
    title: 'Vision',
    body: `SpineDAO is building toward a future where every spine surgeon has access to AI-powered clinical decision support, trained on the largest and most comprehensive spine surgery database ever assembled.

We believe the greatest limitation in medicine today is not technology — it is the isolation of knowledge. Each surgeon works with a finite dataset from their own practice. SpineBase changes this by pooling anonymized clinical data from surgeons and centers worldwide, creating collective intelligence that benefits every patient.

Our goal is to transform spine surgery from isolated experience to collaborative intelligence — where a patient in Tokyo receives the same quality of evidence-based care as a patient in Paris or Boston.`
  },
  problem: {
    title: 'The Problem We Solve',
    body: `Spine surgery data is currently fragmented across institutions, stored in heterogeneous formats, and largely inaccessible for large-scale AI development.

**The reality today:**
- A highly experienced surgeon might perform 200 lumbar fusions in their career — a limited dataset for understanding optimal patient selection or predicting outcomes.
- Rare complications that occur in 1–2% of cases become invisible in small datasets, yet these are precisely where clinical wisdom could prevent harm.
- Surgeons worldwide treat similar patients, face similar challenges, generate similar data — yet this collective knowledge remains trapped in isolated EHRs and spreadsheets.
- AI companies in other fields leverage millions of data points for transformative technologies. Medicine, despite its complexity, lags behind.

The data exists. The clinical experience exists. What was missing is the infrastructure, the standards, and the collective will to pool our knowledge.`
  },
  solution: {
    title: 'Our Solution',
    body: `SpineBase is a production platform at **spinebase.app** that makes collaborative spine surgery research possible today — not in theory, but in practice.

**What's live now:**
- **370+ patients** across two active campaigns: SI Joint Fusion (125 patients) and Open vs MIS vs Endoscopic TLIF International Multicenter Cohort (245 patients)
- **SRC Follow.fr Auto-Detection** — drag & drop CSV exports from any SRC Follow center, instant 79-field mapping at 95% confidence
- **Arm Comparison Analytics** — side-by-side ODI, VAS, and satisfaction charts across surgical approaches
- **Outcome Prediction Engine** — statistical case matching with confidence intervals for surgical planning
- **Center Benchmarking** — compare your center's outcomes against the network
- **Intelligent Data Upload** — AI-powered field mapping from any CSV/Excel format
- **Query Builder** — filter and export patient data with dynamic filters
- **Interactive Analytics** — demographics, outcomes, trajectories

**Core architecture:**
- **Standardized Data Format** — The SpineBase standard provides a unified schema for spine surgery data, enabling seamless contribution from any EHR system worldwide.
- **AI-Powered Transformation** — LLM-based tools automatically convert data from any local format to SpineBase standard, requiring zero manual re-entry.
- **Privacy by Design** — All data is fully anonymized, with HIPAA and GDPR compliance built into every layer.
- **Center-aware Data Model** — Data can be uploaded by individual surgeons or by institutional coordinators on behalf of their center.`
  },
  network: {
    title: 'The Network',
    body: `SpineDAO has brought together 70+ spine surgeons in the spinal.science network. SpineBase currently hosts data from multiple centers with active contribution pipelines.

**Current participants:**
- **IDRISS Périgueux** — 125 SI Fusion patients + 205 Open TLIF patients (founding dataset)
- **SRC Follow Network** — 4 centers contributing TLIF data (Périgueux, Perez, Lebhar, Orthosud)
- **MIS-TLIF Cohort** — 40 patients from minimally invasive approach centers
- **70+ surgeons** invited to contribute across the spinal.science network

**What the data has already revealed:**
- **SI Fusion:** Average 54% improvement at 3 months, 76% satisfaction rate, 5.6% complication rate
- **TLIF Cohort:** 245 patients across Open (n=205) and MIS (n=40) approaches with full follow-up at 1, 3, 6, 12, and 24 months
- Average age 66.6, BMI 28.3, Pre-op ODI 42.5, Pre-op VAS Back 7.2
- 82 min average operative time (range 15–260 min)
- Arm comparison revealing approach-specific outcome trajectories
- Identification of BMI, age, diagnosis, and approach type as key outcome predictors

**The network effect is real:** every new center that contributes data makes the prediction engine more accurate, the benchmarks more meaningful, and the AI models more powerful for everyone.`
  },

  // ═══════════════════ PLATFORM GUIDE ═══════════════════
  overview: {
    title: 'Platform Overview',
    body: `SpineBase is a web-based platform at **spinebase.app** with SSL encryption and role-based access control. Here's what each section does:

**Dashboard** — At-a-glance overview: total patients, active campaigns, recent uploads, and key statistics with interactive charts.

**Campaigns** — Create and manage structured research studies. Each campaign defines a data schema, study type, and outcome endpoints.

**Upload** — Upload patient data in CSV or Excel format. Intelligent field mapping converts your data to SpineBase standard automatically.

**Predict** — The Outcome Prediction Engine. Enter a patient profile and see statistical predictions for improvement, complications, satisfaction, and return to work based on similar cases in the database.

**My Practice** — Center and surgeon benchmarking. Compare your outcomes against the network on complication rates, improvement scores, operative metrics, and more.

**Query** — Filter and explore patient data with dynamic filters, sortable results, and CSV export.

**Analytics** — Interactive visualizations: demographics, outcomes timelines, scatter plots, data quality metrics.

**Users** — Admin panel to manage surgeons, centers, researchers, and access permissions.

**Docs** — You are here.`
  },
  campaigns: {
    title: 'Research Campaigns',
    body: `Campaigns are the organizational unit of SpineBase. Each campaign represents a research study with a defined scope.

**Creating a Campaign:**
1. Click **"+ New Campaign"** from the Campaigns page
2. Fill in study details: name, description, principal investigator, study type
3. Define the data schema — select fields from the SpineBase standard or add custom fields
4. Set outcome endpoints and follow-up timepoints
5. Launch the campaign

**Campaign Types:**
- **Retrospective** — Analyzing previously collected data
- **Prospective** — Collecting data going forward
- **Registry** — Ongoing data collection without a defined endpoint

**Active Campaigns:**
- **Sacroiliac Joint Fusion Outcomes** — 125 patients, tracking ODI, VAS, satisfaction, fusion rates, and return to work at 3, 6, 12, and 24 months.
- **Open vs MIS vs Endoscopic TLIF** — 245 patients, international multicenter comparison with arm-level analytics (ODI, VAS, satisfaction by approach).

Each campaign has its own patient pool, field definitions, analytics dashboard, and prediction models.`
  },
  uploading: {
    title: 'Uploading Data',
    body: `SpineBase accepts data in CSV and Excel formats from any source — Epic, pro.follow.fr, custom spreadsheets, or any EHR export.

**Step-by-step:**
1. Go to the **Upload** page
2. Select the target **campaign**
3. For comparative studies (multi-arm campaigns), select the **target arm** (e.g., "Endo TLIF", "Open TLIF", "MIS TLIF")
4. Drag and drop your file (or click to browse)
5. The system analyzes your columns and suggests field mappings
6. **PHI columns are automatically blocked** — names, emails, birth dates, surgeon identifiers are detected and excluded (shown with a red "PHI" badge)
7. Review and adjust mappings as needed
8. Click **Confirm & Insert** — data is validated, quality-scored, and inserted into the selected arm

**Arm Selection for Comparative Studies:**
When uploading to a campaign with multiple study arms, a dropdown lets you assign all patients in the batch to a specific arm. This ensures that an "Endo TLIF" dataset goes to the Endo TLIF arm, not the default first arm. Each batch upload is a single arm — upload once per arm.

**Intelligent Mapping (threshold: 75%):**
The upload system uses fuzzy matching to map your column headers to SpineBase fields. Only matches above 75% confidence are auto-mapped. For example:
- "Patient Age" → age_at_surgery
- "ODI Score Preop" → preop_odi
- "Date de chirurgie" → surgery_date (French headers supported)
- "Satisfaction 3 mois" → mo3_satisfaction
- "IMC" → bmi (French medical abbreviations recognized)

Columns below the threshold are left unmapped — you can manually assign them from the dropdown.

**Data Validation:**
- ODI scores: 0–100
- VAS scores: 0–10
- Age: 18–100
- BMI: 15–60 (auto-calculated from height/weight if missing)
- Dates: Auto-converted to ISO 8601

**Quality Scoring:**
Each upload receives a quality score (0–100) based on completeness of required fields, follow-up data availability, and validation pass rate.

**Revoking an Upload:**
You can delete any of your previous uploads from the campaign detail page. Go to **Campaigns → select your campaign → Upload History**. Each import shows the filename, patient count, date, and uploader. Click the 🗑 button to permanently delete all patients from that batch. This cannot be undone.

**Who can upload:**
- **Surgeon** accounts — Upload their personal cases
- **Center** accounts — Upload bulk institutional datasets
- **Admin** accounts — Upload on behalf of any center`
  },
  'phi-protection': {
    title: 'PHI Protection System',
    body: `SpineBase includes a multi-layer PHI (Protected Health Information) protection system that automatically detects and blocks personal data during upload.

**Layer 1: Column Name Detection**
Over 30 regex patterns automatically identify PHI columns in your source file:
- **Names:** firstName, lastName, birthName, prénom, nom
- **Contact:** email, phone, téléphone, mobile, address, adresse
- **Identity:** birthDate, date de naissance, SSN, NIR, serial, dossier, MRN
- **Provider:** Chirurgien, surgeon name, doctor, médecin

These columns are **never mapped** to SpineBase fields, regardless of fuzzy matching scores. They appear in the mapping table with a red "PHI" badge and 🚫 icon.

**Layer 2: Confidence Threshold (75%)**
The mapping engine only auto-maps columns when fuzzy matching confidence exceeds 75%. This prevents false positives like a "birthFirstName" column accidentally matching to "bmi" (which scored 64% — below the threshold).

**Layer 3: Value-Level Scanning**
Even if a column passes name detection, individual cell values are scanned for:
- Email addresses (pattern: user@domain.tld)
- Phone numbers (8+ digits with separators)
- Social security numbers (SSN/NIR patterns)

Values matching these patterns are blocked and counted in the import report.

**Layer 4: Confirm-Time Guard**
A final check runs at insertion time. If any active mapping still references a PHI column (e.g., if someone manually overrode the mapping), the system rejects the entire import with a clear error message.

**PHI Warning Banner:**
When PHI columns are detected in your file, a red banner appears at the top of the mapping preview listing exactly which columns were blocked. Example: "🛡️ PHI Protection Active — 5 columns blocked (birthFirstName, birthLastName, email, birthDate, Chirurgien)."

**What to do if PHI slips through:**
If you discover patient-identifiable data in the database, immediately go to **Campaigns → Upload History → 🗑 Delete** for that batch. Contact SpineDAO administrators. The system logs all uploads for audit purposes.`
  },
  'manage-campaigns': {
    title: 'Managing Campaigns',
    body: `Campaigns are the core organizational unit in SpineBase. Each campaign represents a research study with its own schema, arms, and patient data.

**Creating a Campaign:**
Click "New Campaign" on the Campaigns page. A 4-step wizard guides you:
1. **Details** — Name, study type (retrospective/prospective/randomized/registry), principal investigator, primary outcome
2. **Study Arms** — Define treatment groups (e.g., "Open TLIF", "Endo TLIF", "MIS TLIF"). At least one arm required.
3. **Data Fields** — Define your schema. Use the "Spine Surgery Template" for a quick start with standard SpineBase fields, or build custom fields. Each field has a name, type (text/integer/float/categorical/boolean/date), category, and optional validation.
4. **Review** — Confirm everything and create.

**Editing a Campaign:**
Click the ✏️ Edit button on any campaign. You can:
- Change name, description, study type, PI, primary outcome
- Add new study arms or rename existing ones
- Add new data fields
- ⚠️ If the campaign already has patients, you cannot delete existing fields or change their types (data integrity protection). Arms with zero patients can be removed; arms with patients cannot.

**Deleting a Campaign:**
Click the 🗑️ button to delete a campaign.
- **Empty campaigns:** Simple confirmation dialog
- **Campaigns with patients:** You must type the exact campaign name to confirm deletion. This prevents accidental data loss. Deletion is permanent and cascading — all patients, arms, fields, and upload records are removed.

**Deleting Individual Uploads:**
Inside any campaign, scroll to **Upload History** to see all imports. Each entry shows:
- Filename
- Number of patients inserted
- Date and uploader name
- 🗑 Delete button — removes all patients from that specific upload batch

This is how contributors can revoke their data contribution without affecting other uploads in the same campaign.`
  },
  predict: {
    title: 'Outcome Prediction Engine',
    body: `The Prediction Engine is SpineBase's flagship clinical decision support tool. It uses statistical case matching to provide evidence-based outcome observations for surgical planning.

**How it works:**
The engine matches a new patient profile against the entire database using data-driven field weighting. Fields that correlate most strongly with outcomes (determined by Pearson correlation and ANOVA F-tests) receive higher weight in the matching algorithm.

**3-step wizard:**

**Step 1: Patient Demographics**
Enter age, sex, BMI, smoking status, and ASA class. A live sidebar shows how many patients in the database share similar demographics.

**Step 2: Lesion / Pathology**
Specify laterality, prior surgery history, pre-op ODI, VAS scores, and diagnostic injection status. The matching pool narrows in real-time.

**Step 3: Surgical Strategy**
Select approach (percutaneous, mini-open, open) and implant count. Compare strategies side-by-side.

**What you see:**
- **Matched patients count** — How many similar cases exist (typically 15–40)
- **Confidence score** — 0–100 rating based on sample size, data quality, and profile completeness
- **Improvement trajectory** — Expected improvement at 3, 6, 12, and 24 months with 95% confidence intervals
- **Complication risk** — Observed rate with Wilson score intervals
- **Reoperation risk** — Observed rate in matched cohort
- **Satisfaction rates** — Patient-reported satisfaction at each timepoint
- **Return to work** — Timeline for returning to work in matched cases
- **Risk factors** — Top factors influencing outcomes, ranked by impact

**Important guardrails:**
- All predictions use observational language ("observed in similar cases"), never prescriptive language
- Minimum 10 matched patients required for any prediction
- 95% confidence intervals shown on all metrics
- Prominent disclaimer: "Not a medical device. Does not replace clinical judgment."
- Statistical significance flagged explicitly (p-values displayed)`
  },
  benchmark: {
    title: 'My Practice — Benchmarking',
    body: `The My Practice page lets surgeons and centers compare their outcomes against the SpineBase network.

**What's shown:**

**Summary cards:**
- Your patient count vs network total
- Number of contributing centers
- Your complication rate, improvement rate, and satisfaction rate

**Performance radar chart:**
A visual overview plotting your practice across 5 dimensions (improvement, satisfaction, low complications, short op time, short stay) against the network baseline.

**Detailed comparison table:**
12 metrics compared side-by-side with color-coded deltas:
- 🟢 Green — better than network average
- 🔴 Red — below network average
- ⚪ Gray — context metric (no better/worse)

Metrics include: age, BMI, pre-op ODI, VAS, operative time, length of stay, implant count, complication rate, improvement at 3 and 12 months, satisfaction at 3 and 12 months.

**Patient distribution charts:**
Overlay histograms showing your patient population vs network for age, BMI, pre-op ODI, and operative time.

**How data attribution works:**
- **Surgeons who upload their own data** → "My Cases" shown separately, then center, then network
- **Centers uploading bulk data** → Center name shown (e.g., "IDRISS Périgueux", "SRC Follow Perez") vs network
- **When more centers contribute**, the comparison becomes increasingly meaningful

**Data provenance:**
The page shows the source of data transparently — whether it's a personal upload or institutional contribution.`
  },
  querying: {
    title: 'Query & Analytics',
    body: `**Query Builder:**
The Query page lets you filter patients across any field in a campaign.

- Add multiple filters with different operators (equals, greater than, between, contains, etc.)
- Categorical fields show dropdown menus populated with actual values from your data
- Results are sortable by any column
- Export filtered results to CSV for offline analysis

**Analytics Dashboard:**
The Analytics page provides interactive visualizations:
- **Demographics:** Age and BMI histograms, sex and smoking pie charts
- **Surgical Details:** Laterality, approach, ASA classification distributions
- **Outcomes Timeline:** ODI and VAS scores over time (pre-op → 3mo → 6mo → 12mo → 24mo)
- **Scatter Plots:** Age vs. ODI improvement, colored by sex
- **Data Quality:** Completeness distribution across records`
  },
  users: {
    title: 'Users & Centers',
    body: `SpineBase uses a center-aware data model with four user roles.

**Roles:**
- **Admin** — Full platform access: manage users, campaigns, centers, view all data
- **Surgeon** — Upload personal cases, query patients, use prediction engine, view benchmarks
- **Center** — Institutional account for bulk uploads on behalf of a center (e.g., a research coordinator). Same data access as surgeon but uploads attributed to the center, not an individual
- **Researcher** — Query and export anonymized data, view analytics (read-only)

**Centers:**
Centers represent hospitals, clinics, or research institutions. Each center has:
- Name, city, country, type (hospital/clinic/university/research)
- Wallet address (for future $SPINE token distribution)
- Associated users and patient counts

**How data attribution works:**
- When a **surgeon** uploads → patients tagged with surgeon_id + center_id (if assigned)
- When a **center** account uploads → patients tagged with center_id only (bulk institutional data)
- This distinction matters for benchmarking (personal vs institutional) and for future token rewards

**Managing Users (Admin only):**
- Navigate to the **Users** page to see centers and users
- Click **"+ Add Center"** to register a new institution
- Click **"+ Add User"** to create accounts with role and center assignment
- Click **Edit** on any user to change their role or center inline
- Reset passwords or deactivate accounts as needed`
  },

  // ═══════════════════ DATA STANDARD ═══════════════════
  'spinebase-overview': {
    title: 'SpineBase Standard Overview',
    body: `The SpineBase standard (v1.0, January 2026) is a unified data specification for spine surgery data — including sacroiliac joint fusion and lumbar procedures (TLIF, PLIF, ALIF). Designed for seamless integration with LLM-based transformation tools and SRC Follow.fr auto-detection profiles, it enables surgeons worldwide to contribute anonymized data in a consistent format.

**Design Principles:**
- One row per patient with complete longitudinal data
- Wide format for easy analysis and AI training
- Standardized follow-up timepoints (3, 6, 12, 24 months)
- Validated clinical outcome measures (ODI, VAS)
- Comprehensive complication and revision tracking

**Data Categories:**
The standard organizes data into seven primary categories: Patient Demographics, Surgical Intervention Details, Pre-operative Assessment, Operative Details & Complications, Post-operative Follow-up, Revision Surgeries, and Return to Work & Quality of Life.

**Future expansions** planned: lumbar fusion, cervical procedures, deformity surgery, DICOM imaging metadata, SF-36/EQ-5D/PROMIS measures, wearable device data.`
  },
  'data-categories': {
    title: 'Data Categories',
    body: `**1. Demographics**
sex, age_at_surgery, height_m, weight_kg, bmi (calculated), smoking_status (Never/Former/Current <10/day/Current >10/day), occupation_type (Sedentary/Light/Moderate/Heavy/Retired/Disabled), employment_status, workers_comp.

**2. Surgical Intervention**
surgery_date, procedure_name, laterality (Left/Right/Bilateral), implant_count, implant_type, approach (Percutaneous/Mini-open/Open), approach_type (Open/MIS/Endo for TLIF), operative_time_min, blood_loss_ml, length_of_stay_days, asa_class (I/II/III/IV).

**3. Previous & Concurrent Surgeries**
prior_spine_surgery (Yes/No), prior_surgery_details, concurrent_procedures, revision_of_si_fusion (Yes/No).

**4. Pre-operative Assessment**
preop_odi (0–100), preop_vas_back (0–10), preop_vas_leg (0–10), diagnostic_injection (Yes/No), imaging_confirmation (CT/MRI/Bone scan/Multiple), provocation_tests.

**5. Complications**
intraop_complications, early_complications (<30 days), late_complications (>30 days), reoperation (Yes/No), reoperation_details.

**6. Follow-up Data (3, 6, 12, 24 months)**
Each timepoint prefixed (mo3_, mo6_, mo12_, mo24_): visit_date, odi, vas_back, vas_leg, satisfaction (Very satisfied/Satisfied/Neutral/Dissatisfied/Very dissatisfied), improvement_pct (0–100), return_work, return_sport, new_symptoms.

**7. Fusion & Radiographic Outcomes**
fusion_status (Solid/Partial/Pseudarthrosis/Not assessed), implant_position (Optimal/Suboptimal/Malpositioned), adjacent_pathology.

**8. Patient-Reported Outcomes**
would_repeat_surgery (Definitely yes → Definitely no), max_improvement_pct, time_to_max_improvement (months).`
  },
  'required-fields': {
    title: 'Required Fields',
    body: `The following fields are mandatory for inclusion in SpineBase:

**Always required:**
- patient_id (auto-generated), center_id, surgeon_id
- sex, age_at_surgery
- surgery_date, procedure_name, laterality

**Clinical data (at least one):**
- preop_odi, OR
- preop_vas_back AND preop_vas_leg

**Follow-up:**
- At least one follow-up timepoint with ODI or VAS scores

Records missing required fields are flagged during validation and must be completed or excluded.

**Quality scoring impact:**
Each uploaded dataset receives a quality score (0–100). Higher completeness = higher score = more weight in the prediction engine.`
  },
  validation: {
    title: 'Validation Rules',
    body: `SpineBase enforces data quality through automated validation:

| Field | Rule |
|-------|------|
| ODI scores | 0–100 |
| VAS scores | Integers 0–10 |
| BMI | 15–60 (or calculated from height/weight) |
| Age | 18–100 years |
| Dates | Surgery date must precede all follow-up dates |
| Operative time | Positive integer, typically 10–180 minutes |
| Blood loss | Non-negative, typically < 500 ml for SI fusion |
| Improvement % | 0–100 |

**Quality Metrics:**
Each dataset is evaluated on:
- **Completeness** — % of required fields populated
- **Follow-up rate** — % of patients with 12-month or 24-month data
- **Consistency** — % of records passing all validation rules
- **Outlier detection** — Flagging statistically improbable values

**Complication detection:**
The system intelligently filters false positives in complication fields — numeric values, dates, and common "no complication" terms (none, aucune, ras, non) are excluded from complication counts.

**Satisfaction normalization:**
French and English satisfaction labels are normalized: "Satisfait" → "Satisfied", "Tres Satisfait" → "Very Satisfied", etc.`
  },
  transformation: {
    title: 'Data Transformation',
    body: `SpineBase accepts data from any EHR or practice management system. The transformation workflow:

**1. Export** — Extract data from your system in any format (CSV, Excel, database export)
**2. Upload** — Provide the file to SpineBase's upload interface
**3. Auto-Map** — The system intelligently maps your columns to SpineBase fields using fuzzy matching
**4. Review** — Verify the mapping and correct any ambiguities
**5. Validate** — Automated quality checks ensure completeness and accuracy
**6. Contribute** — Validated data is added to the campaign

**Common Conversions Handled Automatically:**
- Date formats: MM/DD/YYYY, DD/MM/YYYY → YYYY-MM-DD
- Height: feet/inches → meters
- Weight: pounds → kilograms
- Sex: M/F → Male/Female
- ASA: 1/2/3/4 → ASA I/II/III/IV
- French terms: Homme/Femme, Gauche/Droite, Satisfait/Très Satisfait, etc.

**All transformation happens inside SpineBase:**
Your data never leaves the platform. The upload system's built-in mapping engine handles column detection, unit conversion, and format standardization automatically. No external AI services are used — your patient data stays on SpineBase's secure infrastructure at all times.

Missing follow-up data is left blank — never imputed.`
  },

  // ═══════════════════ API REFERENCE ═══════════════════
  'api-auth': {
    title: 'Authentication',
    body: `SpineBase uses JWT (JSON Web Token) authentication.

**Login:**
\`POST /api/auth/login\`
Body: \`{ "email": "...", "password": "..." }\`
Returns: \`{ "token": "eyJ...", "user": { "id", "name", "email", "role", "center_id" } }\`

**Usage:**
Include the token in all subsequent requests:
\`Authorization: Bearer <token>\`

**Token payload includes:**
- id — User UUID
- email — User email
- name — Display name
- role — surgeon / center / admin / researcher
- center_id — Associated center UUID (if any)

**Protected routes:**
All /api/v2/* endpoints require a valid JWT token. Admin-only endpoints return 403 for non-admin users.`
  },
  'api-campaigns': {
    title: 'Campaigns API',
    body: `**List campaigns:**
\`GET /api/v2/campaigns\`
Returns: \`{ "campaigns": [...] }\`

**Get campaign details:**
\`GET /api/v2/campaigns/:slug\`
Returns: Campaign info with field definitions and patient count.

**Create campaign (admin):**
\`POST /api/v2/campaigns\`
Body: \`{ "name", "description", "pi", "study_type", "slug" }\`

**Get campaign stats:**
\`GET /api/v2/campaigns/:slug/stats\`
Returns: Demographics summary, outcome averages, follow-up rates.`
  },
  'api-patients': {
    title: 'Patients API',
    body: `**List patients in campaign:**
\`GET /api/v2/campaigns/:slug/patients?limit=50&offset=0\`
Supports filtering via query parameters.

**Upload data:**
\`POST /api/v2/campaigns/:slug/upload\`
Content-Type: multipart/form-data
Body: CSV or Excel file.
Returns: \`{ "patients_inserted", "average_quality" }\`

**Query patients:**
\`POST /api/v2/campaigns/:slug/query\`
Body: \`{ "filters": [{ "field": "age_at_surgery", "op": "gte", "value": 50 }] }\`
Returns: Matching patient records.

**Export CSV:**
\`GET /api/v2/campaigns/:slug/export?format=csv\`
Returns: Downloadable CSV of all patients in campaign.`
  },
  'api-predict': {
    title: 'Prediction API',
    body: `**Run prediction:**
\`POST /api/v2/campaigns/:slug/predict\`

**Body:**
\`{ "age_at_surgery": 55, "sex": "Male", "bmi": 28.0, "smoking_status": "Never", "asa_class": "II", "prior_spine_surgery": "No", "preop_odi": 42, "preop_vas_back": 7, "preop_vas_leg": 5, "approach_type": "Open", "diagnosis_primary": "Spondylolisthésis dégenératif", "num_levels_fused": "1", "fusion_levels": "L4L5" }\`

**Returns:**
- **matched_patients** — Count of similar cases
- **similarity_range** — Min/max similarity scores
- **confidence** — Overall confidence score (0–100) with component breakdown
- **outcomes** — Improvement, satisfaction, return to work at each timepoint with 95% CIs
- **complications** — Observed rate with Wilson score interval
- **risk_factors** — Top factors affecting outcomes, ranked by impact
- **strategy_comparison** — Outcome differences by approach/implant count (when sufficient data)
- **field_importance** — Data-driven variable weights with p-values

**Minimum requirements:**
At least 10 matched patients required for any prediction. Returns error with insufficient data.`
  },
  'api-benchmark': {
    title: 'Benchmark API',
    body: `**Get benchmarks:**
\`GET /api/v2/benchmark/:campaignSlug\`

**Returns:**
- **campaign** — Campaign name
- **surgeon_count** — Number of contributing surgeons
- **center_count** — Number of contributing centers
- **primary_label** — "My Cases" or center name
- **mine** — Metrics for the requesting user's patients/center
- **network** — Metrics for all patients in the campaign

**Metrics included in each set:**
- Demographics: age (mean/median), BMI, sex distribution, laterality
- Pre-op: ODI, VAS back, VAS leg
- Surgery: operative time, length of stay, implant count
- Complications: rate, count
- Outcomes: improvement at 3mo and 12mo, satisfaction at 3mo and 12mo
- Distributions: histograms for age, BMI, pre-op ODI, operative time

**Data attribution logic:**
If the user has personal patients (surgeon_id matches), those are shown as "mine". Otherwise, if the user belongs to a center, that center's data is shown. This handles both individual surgeons and institutional uploads.`
  },
  'api-centers': {
    title: 'Centers API',
    body: `**List centers:**
\`GET /api/v2/centers\`
Returns: All centers with patient count and user count.

**Create center (admin):**
\`POST /api/v2/centers\`
Body: \`{ "name": "Hôpital Privé du Dos", "city": "Lyon", "country": "France", "type": "hospital" }\`

**Center types:** hospital, clinic, university, research

**Each center has:**
- Unique slug (auto-generated from name)
- City and country
- wallet_address field (for future $SPINE token distribution)
- Associated users and patient counts

Centers are the fundamental unit for data attribution, benchmarking, and future token rewards.`
  },

  // ═══════════════════ PRIVACY & LEGAL ═══════════════════
  hipaa: {
    title: 'HIPAA Compliance',
    body: `SpineBase implements HIPAA Safe Harbor de-identification for all contributed data.

**All direct identifiers are removed before storage:**
- Names, addresses, contact information
- Medical record numbers, device identifiers
- Biometric identifiers, photos
- Any unique identifying numbers

**Additional safeguards:**
- System-generated anonymized patient IDs that cannot be reverse-engineered
- Age at surgery used instead of date of birth
- Geographic information limited to country or large region
- Rare data points reviewed for re-identification risk

**Technical measures:**
- AES-256 encryption at rest
- TLS 1.3 encryption in transit (Let's Encrypt SSL)
- Role-based access control with JWT authentication
- Complete audit trail of all data access
- Daily automated backups with 30-day retention`
  },
  gdpr: {
    title: 'GDPR Compliance',
    body: `SpineBase is fully compliant with the EU General Data Protection Regulation.

**Key GDPR measures:**
- **Data Minimization** — Only necessary clinical data is collected; no unnecessary personal information
- **Right to Erasure** — Contributors can request deletion of their contributed data at any time
- **Consent Management** — Clear data usage agreements before any contribution
- **Transparent Processing** — Complete audit trail of all data processing activities
- **Geographic Data Residency** — Platform hosted in HDS-compliant infrastructure
- **Pseudonymization** — All data pseudonymized by default before storage

**Legal Basis for Processing:**
SpineBase processes anonymized data for scientific research purposes under GDPR Article 89, with appropriate safeguards for data subject rights.

**Future HDS certification:**
The Backbone infrastructure (under development) will be deployed on HDS-certified (Hébergeur de Données de Santé) hosting providers compliant with French/EU healthcare regulations.`
  },
  anonymization: {
    title: 'Anonymization Process',
    body: `SpineBase uses a multi-layer anonymization approach:

**Layer 1: De-identification at Source**
- Surgeons/centers remove PHI before upload
- Upload interface flags potential identifiers in free-text fields

**Layer 2: Automated Detection**
- The system scans uploaded data for patterns matching personal identifiers
- Names, dates of birth, addresses, and ID numbers are automatically detected
- Complication fields intelligently filtered to exclude false positives (numeric values, dates)

**Layer 3: System-Generated IDs**
- Original patient identifiers replaced with system-generated UUIDs
- No mapping between original and anonymized IDs stored on SpineBase
- Surgeons maintain their own local mapping for clinical follow-up

**Layer 4: Statistical Review**
- Datasets with very small patient counts reviewed for re-identification risk
- Rare combinations of demographic factors flagged for review
- k-anonymity principles applied (k ≥ 5) for sensitive data combinations`
  },
  'data-usage': {
    title: 'Data Usage Agreement',
    body: `By contributing data to SpineBase, surgeons and institutions agree that:

**Data Usage:**
- Data will be used exclusively for research, AI model development, and improving spine care
- No attempt will be made to re-identify patients
- SpineDAO may share aggregated data with the research community
- Individual center or surgeon performance is never publicly disclosed without consent

**Contributor Rights:**
- Contributors maintain complete control over their data
- Data can be withdrawn at any time by contacting SpineDAO
- Contributors are acknowledged in publications using the aggregated dataset
- Contributors have priority access to AI tools developed from the collaborative database

**Prohibited Uses:**
- Commercial sale of individual patient records
- Re-identification attempts
- Discrimination based on surgeon outcomes
- Use outside of approved research and clinical improvement purposes`
  },
  irb: {
    title: 'IRB & Institutional Review',
    body: `Because SpineBase works with fully anonymized retrospective data, many institutions classify this as exempt from IRB review.

**Typical classification:**
- Retrospective analysis of de-identified data
- No direct patient contact
- No intervention or modification to clinical care
- Minimal risk to subjects

**SpineDAO provides:**
- Template IRB exemption language for your institution
- Data Processing Agreement templates
- Support materials for institutional compliance offices
- Guidance on local regulatory requirements

**For many surgeons, contributing retrospective data requires only:**
1. A brief notification to their institution's research office
2. Confirmation that data is fully de-identified
3. Acknowledgment of the data usage agreement

Contact SpineDAO for assistance navigating your institution's specific requirements.`
  },

  // ═══════════════════ ARCHITECTURE ═══════════════════
  'tech-stack': {
    title: 'Technical Stack',
    body: `**Production Platform (spinebase.app):**
- **Frontend:** React 18 with Recharts for visualizations
- **Backend:** Node.js + Express API server
- **Database:** PostgreSQL with JSONB patient data storage
- **Prediction Engine:** Python 3.12 (numpy, scipy, scikit-learn, psycopg2)
- **Process Manager:** PM2 for Node.js and Python services
- **Web Server:** Nginx reverse proxy with SSL (Let's Encrypt)
- **Hosting:** Ubuntu 24 VPS with daily automated backups

**Security:**
- SSL/TLS encryption on all connections
- JWT authentication with role-based access control
- Password hashing with bcryptjs (10 rounds)
- PostgreSQL row-level data isolation by campaign

**Key architectural decisions:**
- JSONB storage for patient data allows flexible schema per campaign without migrations
- Python prediction engine runs as a subprocess for each request — isolated and stateless
- Center-aware data model separates surgeon vs institutional uploads
- Quality scoring integrated into the upload pipeline`
  },
  'data-model': {
    title: 'Data Model',
    body: `**Core entities:**

**centers** — Hospitals, clinics, institutions
- id (UUID), name, slug, city, country, type, wallet_address

**users** — Surgeons, center coordinators, admins, researchers
- id (UUID), name, email, password_hash, role, center_id, is_active

**campaigns** — Research studies
- id (UUID), name, slug, description, fields (JSONB), study_type

**patients** — Clinical records
- id (UUID), pseudo_id, campaign_id, data (JSONB), quality_score
- surgeon_id (UUID) — who is the operating surgeon
- uploaded_by (UUID) — who uploaded the data
- center_id (UUID) — which institution the data came from

**data_uploads** — Upload history
- id (UUID), campaign_id, user_id, filename, patient_count, quality_score, status

**Relationships:**
- A center has many users and many patients
- A user belongs to one center (optional) and can upload many patients
- A campaign has many patients
- A patient belongs to one campaign, one center (optional), one surgeon (optional)

**Data attribution scenarios:**
- Surgeon uploads own data: surgeon_id = user.id, center_id = user.center_id, uploaded_by = user.id
- Center uploads bulk data: surgeon_id = NULL, center_id = user.center_id, uploaded_by = user.id
- Admin uploads for a center: surgeon_id = NULL, center_id = user.center_id, uploaded_by = user.id`
  },
  'prediction-engine': {
    title: 'Prediction Engine (Technical)',
    body: `The prediction engine is a Python-based statistical matching system.

**Algorithm:**
1. **Field weighting:** Data-driven weights computed from the entire dataset using Pearson correlation (numeric fields) and ANOVA F-tests (categorical fields) against outcome variables
2. **Gaussian similarity:** Each patient in the database is scored against the input profile using a Gaussian kernel. Tighter standard deviations (0.5× σ) ensure meaningful differentiation
3. **Adaptive thresholding:** 60% minimum similarity floor + gap-based clustering. Maximum 40% of dataset can match to prevent "everyone matches" dilution
4. **Statistical computation:** For matched cohort, compute means, medians, 95% confidence intervals (t-distribution for continuous, Wilson score for binary outcomes)
5. **Minimum sample size:** Predictions require n ≥ 10 matched patients. Subgroup analyses require n ≥ 3

**Outcome metrics:**
- Improvement percentage at 3, 6, 12, 24 months
- ODI and VAS reduction from pre-op
- Satisfaction rates (normalized from French/English labels)
- Return to work rates
- Complication risk (with intelligent false-positive filtering)
- Reoperation risk

**Confidence scoring (0–100):**
- Sample size (35%): More matched patients = higher confidence
- Outcome data availability (30%): More follow-up data = higher confidence
- Profile completeness (20%): More input fields = more precise matching
- Dataset size (15%): Larger total dataset = more reliable

**Legal guardrails:**
- All outputs framed as observations ("observed in similar cases"), never recommendations
- Prominent disclaimer on every prediction
- Statistical significance flagged explicitly
- Confidence level clearly labeled (High/Moderate/Low/Very Low)`
  },
  'web3-roadmap': {
    title: 'Web3 & $SPINE Token Roadmap',
    body: `SpineBase is designed for future integration with the Solana blockchain for transparent governance and contributor rewards.

**$SPINE Token (planned):**
- SPL Token on Solana for incentivizing data contributions
- Reward formula: (Patient_Count × BASE_REWARD) × Quality_Multiplier × Follow_Up_Bonus
- Follow-up bonuses: +100 $SPINE/patient at 3mo, +150 at 6mo, +200 at 12mo, +300 at 24mo
- Governance rights: vote on protocol upgrades, research grants, data access

**Soulbound Tokens (SBTs):**
- Non-transferable NFTs for credential-based access control
- SBT Surgeon: verified medical license, upload/vote/earn permissions
- SBT Researcher: academic credentials, query/export access
- SBT Contributor: allied health professionals, limited access

**Infrastructure ready:**
- wallet_address field on centers table (ready for Solana wallet linking)
- Center-aware data model supports institutional vs individual token distribution
- Role system maps to SBT credential tiers
- Quality scoring feeds directly into reward calculations

**Timeline:**
- Phase 1 (current): Production platform with data collection and AI tools
- Phase 2: Solana wallet integration and SBT issuance
- Phase 3: $SPINE token launch and reward distribution
- Phase 4: DAO governance via SPL Governance / Realms`
  },

  // ═══════════════════ FOR SURGEONS ═══════════════════
  'why-join': {
    title: 'Why Join SpineDAO?',
    body: `**The competitive advantage of tomorrow** will not be hoarding data — it will be the ability to leverage collective intelligence to provide better care.

**What you get immediately:**
- **Outcome predictions** — Enter a patient profile and see what happened to similar cases in the database. Useful for surgical planning and patient counseling.
- **Center benchmarking** — See how your complication rates, improvement scores, and satisfaction rates compare to the network. Identify strengths and improvement opportunities.
- **Quality scoring** — Understand the completeness and quality of your data compared to best practices.

**What your data contributes:**
- Every case you add makes the prediction engine more accurate for everyone
- Rare complications become visible when pooled across hundreds of surgeons
- Your surgical outcomes contribute to understanding what works, for whom, and under what circumstances

**Why now:**
- The platform is live and producing real clinical insights from 370+ patients across 2 active campaigns
- Early contributors shape the data standard and platform direction
- $SPINE token rewards will be retroactive — data contributed now earns future tokens
- The network effect is real: more data = better predictions = more value for every surgeon

**This is not about replacing surgeons with algorithms.** It's about augmenting surgical judgment with unprecedented insight, ensuring every patient benefits from the aggregated experience of the entire network.`
  },
  'what-you-get': {
    title: 'What You Receive',
    body: `**Outcome Prediction Engine** — Statistical case matching for surgical planning. Enter a patient profile, see improvement trajectories, complication risks, and satisfaction rates from similar cases.

**Center Benchmarking** — Compare your outcomes to the SpineBase network on 12+ metrics with color-coded performance indicators and distribution overlays.

**AI-Powered Upload** — Intelligent field mapping from any data format. No manual re-entry, no standardization work.

**Research Opportunities** — Collaborate on publications using the pooled dataset. Contributing surgeons are acknowledged as authors on collaborative research.

**$SPINE Tokens (upcoming)** — Earn tokens for contributing data. Higher quality data = more rewards. Use tokens for governance votes, premium features, or research access.

**Professional Network** — Connection to a global community of 70+ spine surgeons committed to evidence-based innovation.

**Legacy** — Be part of building something that will improve spine care for generations.`
  },
  faq: {
    title: 'Frequently Asked Questions',
    body: `**"Will this expose my data or my outcomes?"**
No. All data is anonymized before contribution. Patient identities are protected through HIPAA/GDPR-compliant de-identification. Individual surgeon performance is never publicly disclosed. You maintain complete control and can withdraw at any time.

**"I don't have time to enter more data."**
You don't need to. Continue using your existing EHR. Export your data, upload to SpineBase, and our intelligent mapping handles the conversion. Zero manual re-entry. An institutional coordinator can upload for your entire center.

**"What if my outcomes aren't as good as others?"**
Every surgeon has complications. The goal is not to rank surgeons — it's to understand what works, for whom, and under what circumstances. The benchmarking page is for internal quality improvement only, not public reporting.

**"Can our institution upload data for multiple surgeons?"**
Yes. Create a "center" account for your institution's research coordinator. They upload bulk datasets attributed to the center. Individual surgeons can also create personal accounts to upload their own data separately.

**"Why should I give away my competitive advantage?"**
Surgeons in SpineDAO get access to the prediction engine and benchmarking tools that surgeons outside the network don't. Your competitive advantage grows exponentially by contributing.

**"What about institutional barriers?"**
Because we work with fully anonymized retrospective data, many institutions classify this as exempt from IRB review. SpineDAO provides template language and support materials.

**"What are $SPINE tokens?"**
$SPINE is a planned Solana-based token that will reward data contributors. Contributions made now will be eligible for retroactive token distribution when the token launches.`
  },
};

function Docs() {
  const [activeSection, setActiveSection] = useState('vision');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const content = CONTENT[activeSection];

  const renderMarkdown = (text) => {
    return text.split('\n\n').map((para, i) => {
      if (para.startsWith('|') && para.includes('|')) {
        const rows = para.trim().split('\n').filter(r => !r.match(/^\|[\s-|]+\|$/));
        return (
          <table key={i} className="doc-table">
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>
                  {row.split('|').filter(c => c.trim()).map((cell, ci) => (
                    ri === 0 ? <th key={ci}>{cell.trim()}</th> : <td key={ci}>{cell.trim()}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );
      }
      if (para.startsWith('**') && para.endsWith('**') && !para.includes('\n')) {
        return <h3 key={i} className="doc-subhead">{para.replace(/\*\*/g, '')}</h3>;
      }
      if (para.includes('\n- ')) {
        const parts = para.split('\n');
        const intro = parts[0].startsWith('- ') ? null : parts.shift();
        return (
          <div key={i}>
            {intro && <p className="doc-para" dangerouslySetInnerHTML={{ __html: boldify(intro) }} />}
            <ul className="doc-list">
              {parts.filter(p => p.startsWith('- ')).map((item, j) => (
                <li key={j} dangerouslySetInnerHTML={{ __html: boldify(item.substring(2)) }} />
              ))}
            </ul>
          </div>
        );
      }
      if (/^\d+\./.test(para) && para.includes('\n')) {
        const items = para.split('\n').filter(p => /^\d+\./.test(p));
        return (
          <ol key={i} className="doc-list">
            {items.map((item, j) => (
              <li key={j} dangerouslySetInnerHTML={{ __html: boldify(item.replace(/^\d+\.\s*/, '')) }} />
            ))}
          </ol>
        );
      }
      // Code blocks
      if (para.startsWith('`') && para.endsWith('`') && !para.includes('\n')) {
        return <code key={i} className="doc-code">{para.replace(/`/g, '')}</code>;
      }
      return <p key={i} className="doc-para" dangerouslySetInnerHTML={{ __html: boldify(para) }} />;
    });
  };

  const boldify = (text) => {
    return text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/`(.+?)`/g, '<code class="doc-inline-code">$1</code>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div className="docs-layout">
      <button className="docs-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? '◀' : '▶'} Docs
      </button>
      <aside className={'docs-sidebar' + (sidebarOpen ? ' open' : '')}>
        <div className="docs-brand">
          <h2>SpineBase Docs</h2>
          <p>v1.1 — February 2026</p>
        </div>
        <nav className="docs-nav">
          {SECTIONS.map(section => (
            <div key={section.id} className="nav-section">
              <div className="nav-section-title">{section.icon} {section.title}</div>
              {section.subsections.map(sub => (
                <button
                  key={sub.id}
                  className={'nav-item' + (activeSection === sub.id ? ' active' : '')}
                  onClick={() => { setActiveSection(sub.id); if (window.innerWidth < 900) setSidebarOpen(false); }}
                >
                  {sub.title}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>
      <main className="docs-content">
        <div className="docs-article">
          <h1>{content?.title}</h1>
          {content && renderMarkdown(content.body)}
        </div>
        <div className="docs-footer">
          SpineDAO · spinebase.app · spinal.science · spinedao.com
        </div>
      </main>
    </div>
  );
}
export default Docs;
